import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/checkins";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { errMsg } from "./ResolveDialog";

const FIELDS = ["invite_subject", "invite_message", "reminder_subject", "reminder_message", "button_label"];
const STATUS = {
  default: ["Using default wording", "bg-zinc-100 text-zinc-700"],
  published: ["Custom wording is live", "bg-emerald-100 text-emerald-900"],
  draft_pending: ["Draft has unpublished changes", "bg-amber-100 text-amber-900"],
};

function StatusLine({ w }) {
  const [label, cls] = STATUS[w.status];
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
      <span className={`rounded px-1.5 py-0.5 font-semibold ${cls}`} data-testid="wording-status">{label}</span>
      {w.published_at && <span>Live since {fmtDateTime(w.published_at)}{w.published_by_name ? ` by ${w.published_by_name}` : ""}</span>}
      {w.draft_updated_at && <span>· draft saved {fmtDateTime(w.draft_updated_at)}</span>}
    </div>
  );
}

function PreviewDialog({ open, onClose, preview }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="wording-preview-dialog">
        <DialogHeader><DialogTitle>Draft preview — {preview?.kind === "reminder" ? "Reminder" : "Invitation"}</DialogTitle></DialogHeader>
        {preview && (
          <>
            <div className="text-sm" data-testid="wording-preview-subject"><span className="text-zinc-500">Subject:</span> <b>{preview.subject}</b></div>
            <iframe title="Draft email preview" srcDoc={preview.html} sandbox="" className="h-[520px] w-full rounded border border-zinc-200 bg-white" data-testid="wording-preview-frame" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Editor({ f, set, disabled }) {
  const txt = (k, label, rows) => (
    <div><Label>{label}</Label>
      {rows ? <Textarea rows={rows} value={f[k]} onChange={(e) => set(k, e.target.value)} disabled={disabled} data-testid={`wording-${k}`} />
        : <Input value={f[k]} onChange={(e) => set(k, e.target.value)} disabled={disabled} data-testid={`wording-${k}`} />}
    </div>
  );
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="space-y-3">{txt("invite_subject", "Invitation subject")}{txt("invite_message", "Invitation message", 6)}</div>
      <div className="space-y-3">{txt("reminder_subject", "Reminder subject")}{txt("reminder_message", "Reminder message", 6)}</div>
      <div className="md:col-span-2 max-w-sm">{txt("button_label", "Button text (both emails)")}</div>
    </div>
  );
}

function useWording(tid) {
  const [w, setW] = useState(null);
  const [f, setF] = useState(null);
  const load = (data) => { setW(data); setF({ ...data.defaults, ...(data.published || {}), ...(data.draft || {}) }); };
  useEffect(() => {
    if (!tid) return;
    setW(null);
    api.get(`/checkin-email-wording/${tid}`).then((r) => load(r.data)).catch(() => setW(null));
  }, [tid]);
  return { w, f, setF, load };
}

export function EmailWordingCard({ isLead }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const { w, f, setF, load } = useWording(tid);
  useEffect(() => {
    api.get("/checkin-templates").then((r) => {
      const rows = r.data.filter((t) => t.current_version_id);
      setTemplates(rows);
      if (rows.length) setTid(rows[0].id);
    });
  }, []);
  const tpl = templates.find((t) => t.id === tid);
  async function act(kind, fn, ok) {
    setBusy(kind);
    try { const r = await fn(); if (r?.data?.status) load(r.data); if (ok) toast.success(ok); return r; }
    catch (e) { toast.error("Couldn't update wording", { description: errMsg(e) }); return null; }
    finally { setBusy(null); }
  }
  const save = () => act("save", () => api.put(`/checkin-email-wording/${tid}`, Object.fromEntries(FIELDS.map((k) => [k, f[k]]))), "Draft saved");
  async function previewDraft(kind) {
    const saved = await save();
    if (!saved) return;
    const r = await act("preview", () => api.post("/checkin-email-preview", { kind, version_id: tpl.current_version_id, wording: "draft" }));
    if (r) setPreview({ ...r.data, kind });
  }
  const spin = (k) => busy === k && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />;
  return (
    <Card className="space-y-3 p-4" data-testid="email-wording-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="font-display font-semibold">Email wording</h3>
          <p className="text-xs text-zinc-500">Invitation and reminder text per template. Placeholders: {"{first_name}"} {"{clinic_name}"} {"{minutes}"}. Safety wording, the stop-emails link and sign-off are fixed.</p></div>
        <select value={tid} onChange={(e) => setTid(e.target.value)} className="h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm" data-testid="wording-template-select">
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      {!w || !f ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : (
        <>
          <StatusLine w={w} />
          <Editor f={f} set={(k, v) => setF((x) => ({ ...x, [k]: v }))} disabled={!isLead} />
          {isLead && (
            <div className="flex flex-wrap gap-2 border-t border-zinc-200 pt-3">
              <Button size="sm" variant="outline" disabled={!!busy} onClick={save} data-testid="wording-save">{spin("save")}Save draft</Button>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => previewDraft("invitation")} data-testid="wording-preview-invitation"><Eye className="mr-1.5 h-3.5 w-3.5" />Preview invitation</Button>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => previewDraft("reminder")} data-testid="wording-preview-reminder"><Eye className="mr-1.5 h-3.5 w-3.5" />Preview reminder</Button>
              <Button size="sm" disabled={!!busy || w.status !== "draft_pending"} onClick={() => act("publish", () => api.post(`/checkin-email-wording/${tid}/publish`), "Wording published — new emails use it")} data-testid="wording-publish">{spin("publish")}Publish</Button>
              {w.status === "draft_pending" && (
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act("discard", () => api.post(`/checkin-email-wording/${tid}/discard`), "Draft discarded")} data-testid="wording-discard">Discard draft</Button>
              )}
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setF({ ...w.defaults })} data-testid="wording-reset-defaults">Reset fields to default</Button>
            </div>
          )}
        </>
      )}
      <PreviewDialog open={!!preview} onClose={() => setPreview(null)} preview={preview} />
    </Card>
  );
}
